import { useCallback, useLayoutEffect, useState } from 'react';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { Box, Skeleton, TextFieldProps, TextFieldVariants } from '@mui/material';
import {
  useEditorStore,
  useFlowBuilder2Context,
  useFlowBuilder2FlowValidators,
  FlowBuilder2FlowFieldNamesEnum,
} from '@diana/core';
import { TextFieldWithValidationTooltip } from '../TextFieldWithValidationTooltip/TextFieldWithValidationTooltip';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';
import { useFlowBuilder2PageContext } from '../../Context/useFlowBuilder2PageContext';
import { useEntityFieldState } from '../../Support/useEntityFieldState';

export function FlowNameField<V extends TextFieldVariants>(
  props: TextFieldProps<V> & { autoWidth?: boolean; fontSize?: string },
) {
  const { onChange, onBlur, autoWidth, ...textFieldProps } = props;

  const [value, setValue] = useState<string>('');

  const { updateAndSaveFlow } = useFlowBuilder2Context();

  const getFlow = useEditorStore((s) => s.getFlow);
  const flowId = useEditorStore((s) => s.flow?.id ?? '');
  const flowDisplayName = useEditorStore((s) => s.flow?.properties.displayName ?? '');

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { isLoading: isPageLoading, error: pageError } = useFlowBuilder2PageContext();

  const validators = useFlowBuilder2FlowValidators();
  const fieldState = useEntityFieldState({
    objectId: flowId,
    objectType: 'flow',
    fieldName: FlowBuilder2FlowFieldNamesEnum.DisplayName,
  });

  const validate = validators[FlowBuilder2FlowFieldNamesEnum.DisplayName];

  const validateDisplayName = useCallback(
    (displayName: string) => {
      const flow = getFlow();
      if (!flow || (!fieldState.touched && !displayName)) return;
      const result = validate({
        flow: { ...flow, properties: { ...flow.properties, displayName } },
      });
      if (!isEqual(fieldState.validationResult, result)) fieldState.setValidationResult(result);
    },
    [getFlow, validate, fieldState],
  );

  const saveDisplayName = useDebouncedCallback((displayName: string) => {
    updateAndSaveFlow((draft) => ({ ...draft, properties: { ...draft.properties, displayName } }));
  }, 500);

  useLayoutEffect(() => {
    if (value !== flowDisplayName) setValue(flowDisplayName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowDisplayName]);

  useLayoutEffect(() => {
    return () => saveDisplayName.flush();
  }, [saveDisplayName]);

  if (isPageLoading)
    return (
      <Box width="100%">
        <Skeleton variant="rounded" height={14} width="100%" sx={{ maxWidth: 500 }} />
      </Box>
    );

  if (!pageError)
    return (
      <TextFieldWithValidationTooltip
        placeholder={t('labels.untitledWorkflow')}
        error={fieldState.validationResult?.type === 'error'}
        validationResult={fieldState.validationResult}
        {...textFieldProps}
        autoWidth={autoWidth}
        value={value}
        onChange={(e) => {
          fieldState.emitDirty();
          setValue(e.target.value);
          saveDisplayName(e.target.value);
          validateDisplayName(e.target.value);
          if (onChange) onChange(e);
        }}
        onBlur={(e) => {
          fieldState.emitTouched();
          validateDisplayName(e.target.value);
          if (onBlur) onBlur(e);
        }}
      />
    );

  return null;
}
